/**
 * Types for unified DataChanged realtime event system
 * @see docs/REALTIME_ARCHITECTURE.md
 */

// Entity types that can be received from backend
export type DataChangedEntity =
  | 'renders'
  | 'projects'
  | 'concepts'
  | 'concept_angles'
  | 'angles'
  | 'albums'
  | 'estimates'
  | 'controls'
  | 'user'
  | 'queue'
  | 'purchases'
  | 'loading_facts'
  | 'render_stages'
  | 'references'
  | 'batch';

// Action types
export type DataChangedAction =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'started'
  | 'progress'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'snapshot'
  | 'ready'
  | 'multiple'
  | 'transitioned'
  | 'rejected'
  | 'selection_changed'
  | 'validation_completed';

/**
 * Unified event from backend
 * instant=true: apply payload directly to stores
 * instant=false/undefined: trigger debounced cache invalidation
 */
export interface DataChangedEvent {
  entity: DataChangedEntity | string;
  action: DataChangedAction | string;
  id?: string;
  payload?: Record<string, unknown>;
  instant?: boolean;
  ts: number; // Timestamp in milliseconds
  v: number; // Protocol version
}

/**
 * Batch event payload (entity='batch', action='multiple')
 */
export interface BatchEventPayload {
  events: Array<{
    entity: DataChangedEntity | string;
    action: DataChangedAction | string;
    id?: string;
    payload?: Record<string, unknown>;
  }>;
}

/**
 * Handler for instant events
 * Registered per entity via onInstant(entity, handler)
 */
export type InstantHandler = (event: DataChangedEvent) => void | Promise<void>;

// ============================================================================
// Payload types for type-safe instant handlers
// ============================================================================

export interface RenderStartedPayload {
  status: 'processing';
  started_at: string;
  duration_predicted?: number;
  attempt_number?: number;
  concept_angle_id?: string;
  concept_id?: string;
  project_id?: string;
}

export interface RenderProgressPayload {
  status: string;
  message?: string;
  started_at?: string;
  duration_predicted?: number;
  concept_angle_id?: string;
  concept_id?: string;
  project_id?: string;
}

export interface RenderCompletedPayload {
  status: string;
  completed_at?: string;
  file_url?: string;
  thumbnail_url?: string;
  project_id?: string;
  concept_id?: string;
  concept_angle_id?: string;
  angle_type?: 'main' | 'derivative';
  render_type?: string;
  is_upscaled?: boolean;
  llm_feedback?: Record<string, unknown>;
}

export interface RenderFailedPayload {
  status: 'failed' | 'cancelled';
  error: string;
  code?: string;
  render_type?: string;
  concept_angle_id?: string;
  concept_id?: string;
  project_id?: string;
}

export interface AlbumProgressPayload {
  status: 'generating';
  progress: number;
  message?: string;
  concept_id?: string;
}

export interface AlbumCompletedPayload {
  status: 'ready';
  pdf_url?: string;
  public_url?: string;
  concept_id?: string;
}

export interface AlbumFailedPayload {
  status: 'failed';
  error?: string;
  code?: string;
  concept_id?: string;
}

export interface ControlCompletedPayload {
  status: 'completed';
  file_url?: string;
}

export interface ControlFailedPayload {
  status: 'failed';
  error?: string;
}

export interface UserUpdatedPayload {
  tokens?: number;
  subscription_status?: string;
  reason?: string;
  profile?: Record<string, unknown>;
}

export interface QueueSnapshotPayload {
  jobs: Array<Record<string, unknown>>;
  position?: number;
}

export interface LoadingFactsReadyPayload {
  project_id: string;
  facts: Array<{ id: string; type: string; text: string }>;
  is_contextual: boolean;
}

export interface RenderStagesReadyPayload {
  project_id: string;
  render_id: string;
  stages: string[];
}

export interface TokenPurchasePayload {
  purchase_id?: string;
  token_amount?: number;
  reason?: string;
}

// ============================================================================
// Entity to Query Key mapping for invalidation
// ============================================================================

/**
 * Maps a realtime entity to the TanStack Query key prefixes that must be
 * invalidated when that entity changes. The debounced flush invalidates
 * `[key]` and `[key, 'list']` for every entry, and TanStack Query expands
 * that into any nested key (e.g. `['concepts']` also invalidates
 * `['concepts', 'angles', conceptId]`, which is why angle events fan out
 * through concepts).
 *
 * Keep entries here in sync with the canonical `cacheKeys` factory in
 * `shared/lib/cache-invalidation.ts` (W3.8 — single source of truth for
 * every queryKey root).
 */
export const ENTITY_QUERY_KEYS: Record<string, string[]> = {
  renders: ['renders', 'jobHistory'],
  projects: ['projects'],
  concepts: ['concepts', 'history'],
  // `project-angles` is the Views-library list root (use-project-angles.ts) —
  // without it, plain angles/{created,updated,deleted} events never refresh
  // the library list (only the dedicated validation_completed handler did).
  // `projects` rides along because angle lifecycle changes move the project's
  // server-computed flags (`view_promotion` rescue state, angle counters) —
  // e.g. a validation_completed that auto-routes the upload to References must
  // refresh the project so the Brief rescue banner appears/clears.
  angles: ['angles', 'concepts', 'project-angles', 'projects'],
  // Pivot lifecycle (attach/detach/reorder). Fans out under
  // ['concepts'], which TanStack expands hierarchically to every concept-
  // prefixed query — including `['concepts', conceptId, 'angles']` (the one
  // that actually needs to refetch), plus list/detail queries that ride
  // along. The win over `concepts/updated` is debounce-coalescing of pivot
  // events on the same concept_id, not a narrower invalidation footprint.
  concept_angles: ['concepts'],
  albums: ['albums', 'jobHistory'],
  estimates: ['estimates'],
  controls: ['controls'],
  references: ['project-references'],
  user: ['profile', 'user'],
  queue: ['jobHistory', 'jobQueue'],
  purchases: ['purchases', 'user'],
  loading_facts: ['loading-facts'],
};
